import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useGetExperienceRecordByIdQuery, useUpdateExperienceRecordMutation } from 'store/api/experience-records.ts';
import { ExperienceRecordDetailsDto, ExperienceRecordFormData } from 'types/experience-records.ts';
import { useClassifiers } from 'utils/useClassifiers';
import {
    EXPERIENCE_RECORD_FORM_CLASSIFIERS,
    EXPERIENCE_RECORD_SECTION_FIELDS,
    ExperienceRecordSectionId,
    experienceRecordDtoToForm,
    experienceRecordFormToRequest,
} from '../experienceRecordFormMapping';
import {
    ExperienceRecordCasesFields,
    ExperienceRecordConfidentialAlert,
    ExperienceRecordCoreFields,
    ExperienceRecordDescriptionFields,
    ExperienceRecordMarketingFields,
    ExperienceRecordNameField,
    ExperienceRecordValueFields,
} from './ExperienceRecordFields';
import { ExperienceRecordFormState, useExperienceRecordForm } from './useExperienceRecordForm';

const SECTION_FIELDS: Record<ExperienceRecordSectionId, (form: ExperienceRecordFormState) => ReactNode> = {
    // The name follows the client and the date as they are edited, and is saved with them.
    'core-details': (form) => (
        <>
            <ExperienceRecordCoreFields form={form} />
            <ExperienceRecordNameField form={form} />
        </>
    ),
    // The alert, which no read row shows, follows the fields, so they stay where the card has their rows.
    description: (form) => (
        <>
            <ExperienceRecordDescriptionFields form={form} />
            <ExperienceRecordConfidentialAlert form={form} />
        </>
    ),
    value: (form) => <ExperienceRecordValueFields form={form} />,
    'connected-cases': (form) => <ExperienceRecordCasesFields form={form} />,
    marketing: (form) => <ExperienceRecordMarketingFields form={form} />,
};

interface SectionFormProps {
    section: ExperienceRecordSectionId;
    experienceRecordId: number;
    initialData: ExperienceRecordFormData;
    onClose: () => void;
}

function ExperienceRecordSectionForm({ section, experienceRecordId, initialData, onClose }: SectionFormProps) {
    const form = useExperienceRecordForm(initialData);
    // The page's own cache entry: it reads the record by the route's id.
    const { id = '' } = useParams();
    const { refetch } = useGetExperienceRecordByIdQuery(id);
    const [updateExperienceRecord] = useUpdateExperienceRecordMutation();

    const onSave = (values: ExperienceRecordFormData) =>
        saveSection(values, {
            section,
            sectionFields: EXPERIENCE_RECORD_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto) => experienceRecordDtoToForm(dto, form.classifiers),
            toRequest: (edited) => experienceRecordFormToRequest(edited),
            update: (request) => updateExperienceRecord({ ...request, id: experienceRecordId }).unwrap(),
            onSaved: onClose,
        });

    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

function ExperienceRecordSectionEditor({
    section,
    experienceRecord,
    onClose,
}: {
    section: ExperienceRecordSectionId;
    experienceRecord: ExperienceRecordDetailsDto;
    onClose: () => void;
}) {
    const { classifiers, isLoading } = useClassifiers({ codes: EXPERIENCE_RECORD_FORM_CLASSIFIERS });
    const initialData = useFrozenOnceLoaded(!isLoading, () => experienceRecordDtoToForm(experienceRecord, classifiers));
    if (!initialData) {
        return <Loader />;
    }
    return (
        <ExperienceRecordSectionForm
            section={section}
            experienceRecordId={experienceRecord.id}
            initialData={initialData}
            onClose={onClose}
        />
    );
}

interface ExperienceRecordSectionProps {
    id: ExperienceRecordSectionId;
    titleId: string;
    experienceRecord: ExperienceRecordDetailsDto;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the section edits. */
    rows?: Row[];
}

/** An experience record card on the record page that edits in place with the form's fields for that card. */
export function ExperienceRecordSection({
    id,
    titleId,
    experienceRecord,
    children,
    rows,
}: ExperienceRecordSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => (
                <ExperienceRecordSectionEditor section={id} experienceRecord={experienceRecord} onClose={close} />
            )}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
